import type { components } from './generated/openapi'

/**
 * Envelope canônico de lista (app/core/pagination.py::Page).
 *
 * Escrito à mão, não gerado: o FastAPI emite um schema CONCRETO por item
 * (PageScheduleOut, PageEntryOut, … — 17 deles). Um genérico + os tipos de item
 * dá o mesmo resultado com uma fração do ruído.
 */
export interface Page<T> {
  readonly items: readonly T[]
  readonly total: number
  readonly limit: number
  readonly offset: number
}

export const PAGE_DEFAULT_LIMIT = 50
/** Teto do backend (app/core/pagination.py::MAX_LIMIT). Acima disto: 422. */
export const PAGE_MAX_LIMIT = 1000

/**
 * Tamanhos de página oferecidos no paginador.
 *
 * Fonte ÚNICA: o `<select>` do TablePager e o schema Zod de cada tela derivam
 * daqui. Antes eram duas listas independentes (uma no widget, outra literal no
 * search.ts de schedules) — divergir significava oferecer no select um valor
 * que o `validateSearch` rejeita, e a tela quebrar no clique.
 *
 * O último valor é o teto do backend de propósito: quem concilia o mês inteiro
 * pede 1000 e resolve numa página só.
 */
export const PAGE_SIZES = [50, 100, 200, 500, PAGE_MAX_LIMIT] as const
export type PageSize = (typeof PAGE_SIZES)[number]

/** `order_by` aceita prefixo '-' para decrescente. Allowlist por endpoint. */
export type OrderBy<F extends string> = F | `-${F}`

/**
 * Allowlists de order_by, POR DOMÍNIO.
 *
 * Elas NÃO estão no OpenAPI — vivem nas docstrings dos routers. Valor fora da
 * lista → 422. Codificar como union é o que transforma isso em erro de
 * compilação em vez de erro de runtime.
 */
export type ScheduleOrderBy = OrderBy<
  'due_date' | 'created_at' | 'amount' | 'status' | 'stakeholder'
>
export type EntryOrderBy = OrderBy<'paid_date' | 'created_at' | 'amount'>
export type StakeholderOrderBy = OrderBy<'name' | 'created_at' | 'kind'>
export type CostCenterOrderBy = OrderBy<'name' | 'created_at'>
export type TransferOrderBy = OrderBy<'transfer_date' | 'created_at' | 'amount'>
export type InstallmentPlanOrderBy = OrderBy<
  'first_due_date' | 'created_at' | 'total_amount' | 'installments_count' | 'stakeholder'
>
export type InboxItemOrderBy = OrderBy<
  'created_at' | 'due_date' | 'amount' | 'description' | 'kind' | 'status'
>
/** `due_date` e `amount` vêm do Schedule (JOIN), não da tabela `charge`. */
export type ChargeOrderBy = OrderBy<'created_at' | 'status' | 'due_date' | 'amount' | 'stakeholder'>
export type ServiceInvoiceOrderBy = OrderBy<
  'created_at' | 'status' | 'amount' | 'description' | 'stakeholder'
>

type Schemas = components['schemas']

// Aliases dos tipos gerados — o resto do app NUNCA importa `components` direto.
export type ScheduleOut = Schemas['ScheduleOut']
export type ScheduleCreate = Schemas['ScheduleCreate']
/** Uma baixa do agendamento (aba "Pagamentos recebidos"): conta, data e valor. */
export type ScheduleEntryDetailOut = Schemas['ScheduleEntryDetailOut']
/** Recorrência: materializa N agendamentos futuros (mesmo valor/rateio). */
export type RecurrenceCreate = Schemas['RecurrenceCreate']
export type RecurrenceOut = Schemas['RecurrenceOut']
export type EntryOut = Schemas['EntryOut']
export type StakeholderOut = Schemas['StakeholderOut']
export type CategoryOut = Schemas['CategoryOut']
export type CostCenterOut = Schemas['CostCenterOut']
export type BankAccountOut = Schemas['BankAccountOut']
/** Extrato da conta no período (running balance): envelope com abertura/fechamento + entries paginados. */
export type BankAccountStatementOut = Schemas['BankAccountStatementOut']
export type TenantOut = Schemas['TenantOut']
export type TransferOut = Schemas['TransferOut']
export type InstallmentPlanOut = Schemas['InstallmentPlanOut']
export type InstallmentOut = Schemas['InstallmentOut']
export type CashFlowReportOut = Schemas['CashFlowReportOut']
export type CashFlowPeriodOut = Schemas['CashFlowPeriodOut']
export type ReconciliationOut = Schemas['ReconciliationOut']
export type InboxItemOut = Schemas['InboxItemOut']
export type ChargeOut = Schemas['ChargeOut']
export type ServiceInvoiceOut = Schemas['ServiceInvoiceOut']

/**
 * Contas a pagar/receber COMPOSTAS (`GET /v1/bills`): as duas origens na mesma
 * lista — `schedule` (contábil, deste serviço) e `bank` (transação espelhada de
 * uma integração).
 *
 * O envelope carrega `partial`/`sources`, que a tela é OBRIGADA a exibir: com
 * uma origem fora do ar a lista volta curta, e lista curta é indistinguível de
 * "não tenho contas".
 */
export type BillOut = Schemas['BillOut']
export type BillPageOut = Schemas['BillPageOut']
export type BillSourceStatusOut = Schemas['BillSourceStatusOut']
/** Situação da série BANCÁRIA do fluxo de caixa (só com `include_bank=true`). */
export type CashFlowBankOut = Schemas['CashFlowBankOut']

// Integrações (proxy do processor) — catálogo, cadastro, credencial, teste e sync-runs.
/**
 * Catálogo: quais bancos ESTE deploy sabe integrar e o que cada um pede de
 * credencial. É o que permite a tela ser genérica — nome de campo de fornecedor
 * nunca aparece escrito no front; ele RENDERIZA a partir de `credential_fields`.
 */
export type ProcessorCatalogOut = Schemas['ProcessorCatalogOut']
export type ProcessorCatalogEntryOut = Schemas['ProcessorCatalogEntryOut']
export type CredentialFieldOut = Schemas['CredentialFieldOut']
export type IntegrationOut = Schemas['IntegrationOut']
export type IntegrationCreateIn = Schemas['IntegrationCreateIn']
export type IntegrationUpdateIn = Schemas['IntegrationUpdateIn']
export type CredentialSetIn = Schemas['CredentialSetIn']
export type IntegrationCredentialStatusOut = Schemas['IntegrationCredentialStatusOut']
export type TestConnectionOut = Schemas['TestConnectionOut']
export type SyncRunOut = Schemas['SyncRunOut']
/** Transação bancária lida via processor — fonte das 4 telas de contas. */
export type BankTransactionOut = Schemas['BankTransactionOut']
